import i18n from "@/plugins/i18n";
import { DayTime } from "./day-time";

export class Duration
{
    protected value: number;

    protected constructor(milliseconds: number)
    {
        this.value = Math.round(milliseconds);
    }


    //
    // Getters
    //

    public get milliseconds(): number
    {
        return this.value;
    }

    public get seconds(): number
    {
        return this.milliseconds / 1000;
    }

    public get minutes(): number
    {
        return this.seconds / 60;
    }

    public get hours(): number
    {
        return this.minutes / 60;
    }

    public get days(): number
    {
        return this.hours / 24;
    }


    //
    // Factory methods to instantiate a Duration
    //

    public static fromMilliseconds(ms: number): Duration
    {
        return new this(ms);
    }

    public static fromSeconds(seconds: number): Duration
    {
        return this.fromMilliseconds(seconds * 1000);
    }

    public static fromMinutes(minutes: number): Duration
    {
        return this.fromSeconds(minutes * 60);
    }

    public static fromHours(hours: number): Duration
    {
        return this.fromMinutes(hours * 60);
    }

    public static fromDays(days: number): Duration
    {
        return this.fromHours(days * 24);
    }

    public static fromUnit(value: number, unit: string): Duration
    {
        switch (unit) {
            case "ms":
            case "milliseconds":
                return this.fromMilliseconds(value);

            case "s":
            case "sec":
            case "seconds":
                return this.fromSeconds(value);

            case "min":
            case "minutes":
                return this.fromMinutes(value);

            case "h":
            case "hours":
                return this.fromHours(value);

            case "d":
            case "days":
                return this.fromDays(value);

            default:
                throw new Error(`Unrecognized time unit: ${unit}`);
        }
    }

    public static fromString(value: string): Duration
    {
        const dayTime = DayTime.makeFromString(value);

        return this.fromSeconds(dayTime.seconds + dayTime.minutes * 60 + dayTime.hours * 60 * 60);
    }

    //
    // Other utility methods
    //

    public toString(): string
    {
        // Ignore milliseconds, we will probably never need them anywhere
        let tmpValue = Math.round(this.value / 1000);

        const sec = tmpValue % 60;
        tmpValue = Math.floor(tmpValue / 60);

        const min = tmpValue % 60;
        tmpValue = Math.floor(tmpValue / 60);

        const hours = tmpValue;

        return `${hours}:${min}:${sec}`;
    }

    public toTextString(): string
    {
        const stringComponents: string[] = [];
        let tmpValue = this.value / 1000;

        if (tmpValue % 60 > 0) {
            stringComponents.push(`${tmpValue % 60} ${i18n.global.t('unit.sec')}`);
        }

        tmpValue = Math.floor(tmpValue / 60);
        
        if (tmpValue % 60 > 0) {
            stringComponents.push(`${tmpValue % 60} ${i18n.global.t('unit.min')}`);
        }
        
        tmpValue = Math.floor(tmpValue / 60);
        
        if (tmpValue % 24 > 0) {
            stringComponents.push(`${tmpValue % 24} ${i18n.global.t('unit.h')}`);
        }

        tmpValue = Math.floor(tmpValue / 24);

        if (tmpValue > 0) {
            stringComponents.push(`${tmpValue} ${i18n.global.t('unit.days')}`);
        }

        return stringComponents.reverse().join(", ");
    }

    /**
     * Get the smallest time unit in which the duration is an integer
     * (no decimals).
     * 
     * @returns             A unit string that can be parsed back by fromUnit()
     */
    public getSmallestRoundUnit(): string
    {
        if (this.days % 1 === 0) {
            return "days";
        }

        if (this.hours % 1 === 0) {
            return "h";
        }

        if (this.minutes % 1 === 0) {
            return "min";
        }

        if (this.seconds % 1 === 0) {
            return "sec";
        }

        return "ms";
    }

    /**
     * Get the date that is before another one by the amount of time of this
     * duration object.
     * 
     * @param date          The reference date
     */
    public subFrom(date: Date): Date
    {
        return new Date(date.getTime() - this.value);
    }

    /**
     * Get the date that is after another one by the amount of time of this
     * duration object.
     * 
     * @param date          The reference date
     */
    public addTo(date: Date): Date
    {
        return new Date(date.getTime() + this.value);
    }

    /**
     * Given a reference date (toAlignWith), get a date that is alinged
     * on the closest anterior multiple of this duration from another date
     * (toAlign).
     * 
     * @param toAlign       The date we wants to align
     * @param toAlignWith   A point in time where to base the durations on
     */
    public alignedDateBefore(toAlign: Date, toAlignWith: Date): Date
    {
        let aligned = toAlignWith;

        if (toAlign >= aligned) {
            for (; this.addTo(aligned) <= toAlign; aligned = this.addTo(aligned));
        }
        else {
            for (; aligned > toAlign; aligned = this.subFrom(aligned));
        }

        return aligned;
    }

    /**
     * Given a reference date (toAlignWith), get a date that is alinged
     * on the closest posterior multiple of this duration from another date
     * (toAlign).
     * 
     * @param toAlign       The date we wants to align
     * @param toAlignWith   A point in time where to base the durations on
     */
    public alignedDateAfter(toAlign: Date, toAlignWith: Date): Date
    {
        let aligned = toAlignWith;

        if (toAlign <= aligned) {
            for (; this.subFrom(aligned) >= toAlign; aligned = this.subFrom(aligned));
        }
        else {
            for (; aligned < toAlign; aligned = this.addTo(aligned));
        }

        return aligned;
    }

    public inUnit(unit: string): number
    {
        switch (unit) {
            case "ms":
            case "milliseconds":
                return this.milliseconds;

            case "s":
            case "sec":
            case "seconds":
                return this.seconds;

            case "min":
            case "minutes":
                return this.minutes;

            case "h":
            case "hours":
                return this.hours;

            case "d":
            case "days":
                return this.days;

            default:
                throw new Error(`Unrecognized time unit: ${unit}`);
        }
    }
}
